import React from 'react';

import { List } from '@material-ui/core';

import SettingCard, { ISetting } from './SettingCard';
import SettingSectionTitle, { ISettingTitle } from './SettingSectionTitle';
import styles from './SettingsList.module.scss';

export type SettingsListItems = Array<ISetting | ISettingTitle>;

export interface SettingsListProps {
	items: SettingsListItems;
	className?: string;
	settingCardClassName?: string;
	noChevron?: boolean;
	noGrouping?: boolean;
}

export const SettingsList: React.FC<SettingsListProps> = ({
	items,
	className,
	settingCardClassName,
	noChevron,
	noGrouping,
}) => {
	return (
		<List className={className || ''}>
			{items.map((item, index, items) => {
				if (item.type === 'title') {
					return (
						<SettingSectionTitle key={`title-${index}`} {...item} />
					);
				} else if (item.type === 'setting') {
					const condition =
						!item.hidden &&
						(typeof item.condition === 'undefined' || item.condition);

					let orderPosition = noGrouping
						? ('middle' as const)
						: getOrderPosition(items, index);
					if (item.orderPosition) {
						orderPosition = item.orderPosition;
					}

					return condition ? (
						<React.Fragment key={`setting-${index}`}>
							<SettingCard
								orderPosition={orderPosition}
								{...item}
								noChevron={noChevron ?? item.noChevron}
								className={settingCardClassName}
							/>
							{hasNextSettingInGroup(items, index) ? (
								<div className={styles['divider-wrapper']}>
									<div className={styles['divider']} />
								</div>
							) : null}
						</React.Fragment>
					) : null;
				}
				return null;
			})}
		</List>
	);
};

const getOrderPosition = (items: SettingsListItems, index: number) => {
	const hasPrev = !!findVisibleSetting(items, index, -1);
	const hasNext = !!findVisibleSetting(items, index, 1);
	if (hasPrev && hasNext) return 'middle' as const;
	if (hasPrev) return 'last' as const;
	if (hasNext) return 'first' as const;
	return 'only' as const;
};

const findVisibleSetting = (items: SettingsListItems, index: number, direction: 1 | -1): ISetting | null => {
	for (let i = index + direction; i >= 0 && i < items.length; i += direction) {
		if (items[i].type === 'title') return null;
		if (isSetting(items[i])) return items[i] as ISetting;
	}
	return null;
};


const hasNextSettingInGroup = (items: SettingsListItems, index: number): boolean => {
	for (let i = index + 1; i < items.length; i++) {
		if (items[i].type === 'title') return false;
		if (isSetting(items[i])) return true;
	}
	return false;
};

const isSetting = (item: ISetting | ISettingTitle): item is ISetting => {
	return (
		item?.type === 'setting' &&
		!item.hidden &&
		(typeof item.condition === 'undefined' || item.condition)
	);
};

export default SettingsList;
